/**
* Helpers PWA — detección de plataforma, estado de instalación y manejo
* del prompt diferido (beforeinstallprompt). Migrado tal cual de la
* app legacy (100% cliente, sin SSR involucrado).
*/
export type Platform =
| "ios-safari"
| "ios-other"
| "android-chromium"
| "desktop-chromium"
| "desktop-firefox"
| "desktop-safari"
| "unknown";
export interface BeforeInstallPromptEvent extends Event {
prompt: () => Promise<void>;
userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}
export interface InstallState {
platform: Platform;
isInstalled: boolean;
canPromptNatively: boolean;
needsManualInstructions: boolean;
}
export function detectPlatform(): Platform {
if (typeof window === "undefined" || typeof navigator === "undefined") {
return "unknown";
}
const ua = navigator.userAgent;
const isIOS =
/iPad|iPhone|iPod/.test(ua) ||
(navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
if (isIOS) {
const isSafariIOS = !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
return isSafariIOS ? "ios-safari" : "ios-other";
}
const isAndroid = /Android/.test(ua);
const isChromium = /Chrome|CriOS|Edg|EdgiOS|Brave/.test(ua);
const isFirefox = /Firefox/.test(ua);
const isSafariDesktop = /Safari/.test(ua) && !/Chrome|CriOS|Edg/.test(ua);
if (isAndroid && isChromium) return "android-chromium";
if (!isAndroid && isChromium) return "desktop-chromium";
if (!isAndroid && isFirefox) return "desktop-firefox";
if (!isAndroid && isSafariDesktop) return "desktop-safari";
return "unknown";
}
export function isRunningStandalone(): boolean {
if (typeof window === "undefined") return false;
if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
const nav = window.navigator as Navigator & { standalone?: boolean };
if (nav.standalone === true) return true;
return false;
}
export function getInstallState(
deferredPrompt: BeforeInstallPromptEvent | null,
): InstallState {
const platform = detectPlatform();
const isInstalled = isRunningStandalone();
return {
platform,
isInstalled,
canPromptNatively: !isInstalled && deferredPrompt !== null,
needsManualInstructions:
!isInstalled &&
(platform === "ios-safari" || platform === "ios-other"),
};
}
export interface InstallInstructions {
title: string;
steps: Array<{ icon: "share" | "plus" | "check" | "safari"; text: string }>;
footerNote?: string;
}
export function getInstallInstructions(platform: Platform): InstallInstructions | null {
if (platform === "ios-safari") {
return {
title: "Instala CaptaAI en tu iPhone",
steps: [
{ icon: "share", text: "Toca el botón Compartir en la barra inferior de Safari" },
{ icon: "plus", text: "Desplázate y toca 'Agregar a pantalla de inicio'" },
{ icon: "check", text: "Toca 'Agregar' en la esquina superior derecha" },
],
footerNote: "CaptaAI aparecerá como una app más en tu pantalla de inicio.",
};
}
if (platform === "ios-other") {
return {
title: "Abre en Safari para instalar",
steps: [
{ icon: "safari", text: "Copia esta dirección y ábrela en Safari" },
{ icon: "share", text: "En Safari, toca el botón Compartir" },
{ icon: "plus", text: "Selecciona 'Agregar a pantalla de inicio'" },
],
footerNote: "iOS solo permite instalar PWAs desde Safari.",
};
}
return null;
}
// ── Dismissal persistence ────────────────────────────────────────
const DISMISSED_KEY = "pwa-install-dismissed";
const DISMISSED_TTL_DAYS = 7;
export function wasRecentlyDismissed(): boolean {
if (typeof window === "undefined") return false;
const raw = window.localStorage.getItem(DISMISSED_KEY);
if (!raw) return false;
const when = Number(raw);
if (!Number.isFinite(when)) return false;
const ageMs = Date.now() - when;
return ageMs < DISMISSED_TTL_DAYS * 24 * 60 * 60 * 1000;
}
export function markDismissed(): void {
if (typeof window === "undefined") return;
window.localStorage.setItem(DISMISSED_KEY, String(Date.now()));
}
export function shouldAutoPromptInstall(state: InstallState): boolean {
if (state.isInstalled) return false;
if (wasRecentlyDismissed()) return false;
return state.canPromptNatively || state.needsManualInstructions;
}
const POST_SUCCESS_KEY = "pwa-post-success-triggered";
/**
* Llamar después del primer batch de upload exitoso para mostrar el
* prompt de instalación. Solo dispara una vez por instalación de
* navegador y respeta el dismiss de 7 días.
*/
export function triggerPostSuccessInstall(
state: InstallState,
openInstall: () => Promise<void>,
): void {
if (state.isInstalled) return;
if (wasRecentlyDismissed()) return;
if (typeof window === "undefined") return;
if (window.localStorage.getItem(POST_SUCCESS_KEY)) return;
if (!state.canPromptNatively && !state.needsManualInstructions) return;
window.localStorage.setItem(POST_SUCCESS_KEY, String(Date.now()));
void openInstall();
}
